class SearchResultsByType {
  static GROUP_TYPES = ['product', 'article', 'page'];
  static activeController = null;

  constructor(container = null) {
    this.container = container || document.getElementById('product-grid');
    if (!SearchResultsByType.shouldCombine(this.container)) return;

    this.sectionId = this.container.dataset.id;
    this.searchUrl = this.container.dataset.searchUrl || window.location.pathname;
    this.stringsEl = document.getElementById('search-results-by-type-strings');
    if (!this.sectionId || !this.stringsEl) return;

    this.strings = JSON.parse(this.stringsEl.textContent);
    this.template = this.container.closest('.template-search');
    this.status = document.getElementById('SearchStatusCount');
    this.noResults = document.getElementById('SearchNoResults');
    this.countDesktop = document.getElementById('ProductCountDesktop');
    this.countMobile = document.getElementById('ProductCount');
    this.load();
  }

  static shouldCombine(container) {
    if (container?.dataset.combineTypes !== 'true') return false;
    return !new URLSearchParams(window.location.search).has('type');
  }

  static refresh() {
    const container = document.getElementById('product-grid');
    if (!SearchResultsByType.shouldCombine(container)) return;
    new SearchResultsByType(container);
  }

  static installFacetHook() {
    if (SearchResultsByType._facetHookInstalled || typeof FacetFiltersForm === 'undefined') return;
    SearchResultsByType._facetHookInstalled = true;

    const original = FacetFiltersForm.renderProductGridContainer;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      original.call(FacetFiltersForm, html);
      SearchResultsByType.refresh();
    };
  }

  buildParamsForType(type) {
    const params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.set('type', type);
    params.delete('page');
    params.append('section_id', this.sectionId);
    return params;
  }

  async load() {
    SearchResultsByType.activeController?.abort();
    this.abortController = new AbortController();
    SearchResultsByType.activeController = this.abortController;

    this.container.classList.add('loading');
    this.container.classList.remove('search-results--ready');
    this.container.setAttribute('aria-busy', 'true');
    this.toggleCountLoading(true);

    try {
      const groups = await Promise.all(
        SearchResultsByType.GROUP_TYPES.map((type) => this.fetchType(type))
      );

      if (this.abortController.signal.aborted) return;

      const visibleGroups = groups.filter((group) => group.count > 0 && group.list);

      if (visibleGroups.length) {
        this.render(visibleGroups);
      } else {
        this.renderEmpty();
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error(error);
    } finally {
      if (this.abortController.signal.aborted) return;
      this.container.classList.remove('loading');
      this.container.classList.add('search-results--ready');
      this.container.removeAttribute('aria-busy');
      this.toggleCountLoading(false);
    }
  }

  async fetchType(type) {
    const params = this.buildParamsForType(type);
    const response = await fetch(`${this.searchUrl}?${params.toString()}`, {
      signal: this.abortController.signal,
    });
    if (!response.ok) throw new Error(response.status);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.getElementById('product-grid');
    const list = grid?.querySelector('ul.product-grid');
    const pagination = grid?.querySelector('.pagination-wrapper');

    list?.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.remove('scroll-trigger', 'animate--slide-in');
      element.removeAttribute('data-cascade');
      element.style.removeProperty('--animation-order');
    });

    return {
      type,
      count: Number(grid?.dataset.resultsCount || 0),
      list,
      pagination,
    };
  }

  headingFor(type) {
    if (type === 'product') return this.strings.products;
    if (type === 'article') return this.strings.blog;
    return this.strings.pages;
  }

  resultsCountText(count) {
    if (count === 1) return this.strings.results_one;
    return this.strings.results_other.replace('987654321', String(count));
  }

  resultsCountSimpleText(count) {
    if (count === 1) return this.strings.results_count_one;
    return this.strings.results_count_other.replace('987654321', String(count));
  }

  toggleCountLoading(isLoading) {
    [this.countDesktop, this.countMobile].forEach((element) => {
      if (!element) return;
      element.closest('.product-count')?.classList.toggle('loading', isLoading);
    });
  }

  updateCounts(total) {
    const summaryText = this.resultsCountText(total);
    const simpleText = this.resultsCountSimpleText(total);

    if (this.status) {
      this.status.hidden = false;
      this.status.textContent = summaryText;
    }

    if (this.countDesktop) this.countDesktop.textContent = simpleText;
    if (this.countMobile) this.countMobile.textContent = simpleText;
  }

  render(groups) {
    const total = groups.reduce((sum, group) => sum + group.count, 0);
    const overlay = this.container.querySelector('.loading-overlay');
    const fragment = document.createDocumentFragment();

    if (overlay) fragment.appendChild(overlay);

    groups.forEach((group) => {
      const section = document.createElement('div');
      section.className = 'template-search__group';

      const heading = document.createElement('h2');
      heading.className = 'template-search__group-heading h4';
      heading.textContent = `${this.headingFor(group.type)} (${group.count})`;
      section.appendChild(heading);
      section.appendChild(group.list);
      if (group.pagination) section.appendChild(group.pagination);
      fragment.appendChild(section);
    });

    this.container.replaceChildren(fragment);
    this.container.classList.remove('collection--empty');
    this.updateCounts(total);

    if (this.noResults) this.noResults.hidden = true;
    if (total > 0) this.template?.classList.remove('template-search--empty');
  }

  renderEmpty() {
    this.updateCounts(0);
    if (this.noResults) this.noResults.hidden = false;
    this.template?.classList.add('template-search--empty');
  }
}

SearchResultsByType.installFacetHook();

(function initSearchResults() {
  const container = document.getElementById('product-grid');
  if (container?.dataset.combineTypes === 'true' && !SearchResultsByType.shouldCombine(container)) {
    container.classList.add('search-results--ready');
    container.classList.remove('loading');
    container.removeAttribute('aria-busy');
  }
  new SearchResultsByType();
})();
